import _ from 'underscore';
import Promise from 'bluebird';

export class InjectionContext {
  /**
   * A dependency injection context
   */
  constructor({ disableDependencyCheck = true } = {}) {
    this.registry = {};
    this.punchCards = [];

    if (disableDependencyCheck) { // eslint-disable-line no-undef
      this.enableDependencyCheck = false;
    } else {
      this.enableDependencyCheck = true;
    }
  }

  /**
   * Get all exports from a dependency
   * @param {string} name - The name of the dependency
   * @returns {*} - The export object of the dependency
   */
  require(name) {
    if (!_.has(this.registry, name)) {
      throw new Error(`Dependency undefined: ${name}`);
    }
    _.each(this.punchCards, card => _.extend(card, { [name]: true }));

    return this.registry[name];
  }

  /**
   * Get the default export from a dependency
   * @param {string} name - The name of the dependency
   * @returns {*} - The default export of the dependency
   */
  requireDefault(name) {
    return this.require(name).default;
  }

  /**
   * Define a feature with dependency injection
   * @param {string[]} dependencies - The name of the dependencies
   * @param {FeatureFactoryCallback} factory -
   *    A callback to fetch the feature exports when dependencies are ready
   * @return {DependencyInjectionCallback} -
   *    A callback to inject dependencies and get the feature exports
   */
  define(dependencies, factory) {
    /**
     * @callback DependencyInjectionCallback
     * @param {Object} modules -
     *    The dependency loader. It could be a LoaderCallback function in form
     *    of an AMD loader (e.g. the `require` function of RequireJS), or a hash
     *    of dependencies.
     * @returns {Promise} - A promise of the feature exports
     */
    return (modules = {}) => {
      const injectedDeps = _.keys(modules);
      const missingDeps = _.difference(dependencies, injectedDeps);

      if (!_.isEmpty(missingDeps) && this.enableDependencyCheck) {
        throw new Error(`Insufficient dependency injection: ${missingDeps.join(', ')}`);
      }

      const sparingDeps = _.difference(injectedDeps, dependencies);

      if (!_.isEmpty(sparingDeps) && this.enableDependencyCheck) {
        throw new Error(`Redundant dependency injection: ${sparingDeps.join(', ')}`);
      }

      const conflictDeps = _.filter(
        dependencies,
        dep => _.has(this.registry, dep) && this.registry[dep] !== modules[dep]
      );

      if (!_.isEmpty(conflictDeps) && this.enableDependencyCheck) {
        throw new Error(`Inconsistent dependency injection: ${conflictDeps.join(', ')}`);
      }

      _.extend(this.registry, modules);

      const card = {};

      this.punchCards.push(card);

      /**
       * @callback FeatureFactoryCallback
       * @param {InjectionContext} context - The dependency injection context
       * @returns {Promise} - A promise of the feature exports
       */
      return Promise.resolve(Promise)
        .then(factory)
        .then(null, (e) => {
          throw e;
        })
        .finally(() => {
          this.punchCards = _.without(this.punchCards, card);

          const unusedDeps = _.difference(dependencies, _.keys(card));

          if (!_.isEmpty(unusedDeps) && this.enableDependencyCheck) {
            throw new Error(`Unused dependency injection: ${unusedDeps.join(', ')}`);
          }
        });
    };
  }
}
